import type { ReactNode } from 'react'
import { Fragment } from 'react'

import { cn } from '@/shared/lib'

type TopbarPillItem = {
  id: string
  /** Short uppercase text — a locale, an environment. Mutually exclusive with `icon`. */
  text?: string
  icon?: ReactNode
  /**
   * A slot that owns its own behaviour — a tile that opens a menu — rendered in
   * place of the default button. It wears `TOPBAR_PILL_TILE` itself and names
   * itself, so `label`, `isActive` and `onSelect` do not reach it. Same escape
   * hatch as `leading`, for a tile that has to keep its position in the row.
   */
  node?: ReactNode
  /** Required when the slot shows an icon: it is the slot's only accessible name. */
  label: string
  isActive?: boolean
}

type TopbarPillProps = {
  items: TopbarPillItem[]
  onSelect?: (id: string) => void
  /** Names the group for screen readers — «Быстрые действия». */
  label: string
  /**
   * A slot that owns its own behaviour — a tile that opens a popover, or one
   * that carries a count. It sits first, among the plain items, and should
   * wear `TOPBAR_PILL_TILE` so it is the same tile as the rest.
   */
  leading?: ReactNode
  className?: string
}

/**
 * The floating bar in the page’s top-right corner: language, theme,
 * notifications, sign-out.
 *
 * **Deviation from the design system.** Verdant draws this as a pill — radius
 * 999 with 34px slots — while the dock is a 20-radius capsule of 40px tiles.
 * Two pieces of chrome in the same material but different silhouettes read as
 * two unrelated things on one screen, so this is the dock laid on its side:
 * same glass, same elevation, same radii, same tiles. Keep the two in step.
 */
const TILE =
  'relative flex size-10 shrink-0 items-center justify-center rounded-xl border press [&_svg]:size-[17px]'

/** The idle tile, for a `leading` slot that has to match the plain items. */
export const TOPBAR_PILL_TILE = cn(
  TILE,
  'border-transparent text-fg-secondary hover:border-border-strong hover:bg-surface-2 hover:text-foreground',
)

export function TopbarPill({ items, onSelect, label, leading, className }: TopbarPillProps) {
  return (
    <div
      aria-label={label}
      data-slot="topbar-pill"
      className={cn(
        // Matches the dock exactly: same fill, same border, same glass.
        'inline-flex items-center gap-1.5 rounded-[20px] border border-border bg-surface-1 px-2.5 py-2 backdrop-blur-[20px] shadow-dock',
        className,
      )}
    >
      {leading}
      {items.map((item) =>
        item.node !== undefined ? (
          <Fragment key={item.id}>{item.node}</Fragment>
        ) : (
          <button
            key={item.id}
            type="button"
            title={item.label}
            aria-label={item.label}
            aria-pressed={item.isActive}
            onClick={() => onSelect?.(item.id)}
            className={cn(
              TILE,
              item.text !== undefined && 'w-auto px-3 text-caption font-semibold tracking-[0.06em]',
              item.isActive === true
                ? 'border-primary/35 bg-primary/16 text-primary inset-shadow-[0_1px_0_rgb(255_255_255/0.12)]'
                : 'border-transparent text-fg-secondary hover:border-border-strong hover:bg-surface-2 hover:text-foreground',
            )}
          >
            {item.text ?? item.icon}
          </button>
        ),
      )}
    </div>
  )
}
